import { getSupertoken } from './mailService';
import { QuestionnaireFormData } from '../ui/pages/questionnaire/context/QuestionnaireFormContext';
import { getImage, clearAllImages, ImageKey, StorageContext } from './indexedDBService';

interface ImagePushEntry {
    key: ImageKey;
    path: string;
    name: string;
    formHasImage: boolean;
}

const GITHUB_API_BASE = 'https://api.github.com';
const REPO_OWNER = 'Milszym';
const REPO_NAME = 'weddie-site';
const BASE_BRANCH = 'live-demo';
const STORAGE_KEY = 'questionnaire-form-data';

interface GitHubFileContent {
    path: string;
    content: string;
}

/**
 * Generates a branch name from form data
 * Format: firstname-secondname-YYYYMMDD-HHMMSS
 */
const generateBranchName = (formData: QuestionnaireFormData): string => {
    // @ts-ignore
    const coupleName1 = formData.basicInfo.coupleName1.toLowerCase().replace(/\s+/g, '-');
    // @ts-ignore
    const coupleName2 = formData.basicInfo.coupleName2.toLowerCase().replace(/\s+/g, '-');
    const now = new Date();
    
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    
    return `${coupleName1}-${coupleName2}-${year}${month}${day}-${hours}${minutes}${seconds}`;
};

const baseImagePushPath = 'public/images';
const baseImagePath = '/images';
const baseMainConfigPath = 'src/mainConfig';

/**
 * Prepares form data for GitHub by replacing images with paths
 */
const prepareFormDataForGitHub = (formData: QuestionnaireFormData): any => {
    const configData = {
        ...formData,
        mainImages: {
            desktopImage: formData.mainImages.desktopImage ? `${baseImagePath}/desktopImage.png` : '',
            mobileImage: formData.mainImages.mobileImage ? `${baseImagePath}/mobileImage.png` : ''
        },
        witnesses: {
            enabled: formData.witnesses.enabled,
            witness1: {
                name: formData.witnesses.witness1.name,
                image: formData.witnesses.witness1.image ? `${baseImagePath}/witness1.png` : ''
            },
            witness2: {
                name: formData.witnesses.witness2.name,
                image: formData.witnesses.witness2.image ? `${baseImagePath}/witness2.png` : ''
            }
        }
    };
    
    return configData;
};

/**
 * Gets the SHA of the base branch
 */
const getBaseBranchSha = async (token: string): Promise<string> => {
    const response = await fetch(
        `${GITHUB_API_BASE}/repos/${REPO_OWNER}/${REPO_NAME}/git/ref/heads/${BASE_BRANCH}`,
        {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Accept': 'application/vnd.github.v3+json'
            }
        }
    );
    
    if (!response.ok) {
        throw new Error(`Failed to get base branch: ${response.statusText}`);
    }
    
    const data = await response.json();
    return data.object.sha;
};

/**
 * Creates a new branch from the base branch
 */
const createBranch = async (token: string, branchName: string, sha: string): Promise<void> => {
    const response = await fetch(
        `${GITHUB_API_BASE}/repos/${REPO_OWNER}/${REPO_NAME}/git/refs`,
        {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Accept': 'application/vnd.github.v3+json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                ref: `refs/heads/${branchName}`,
                sha: sha
            })
        }
    );
    
    if (!response.ok) {
        throw new Error(`Failed to create branch: ${response.statusText}`);
    }
};

/**
 * Converts base64 image to blob and then back to base64 without data URL prefix
 */
const extractBase64Content = (base64String: string): string => {
    // Remove data URL prefix if present (e.g., "data:image/png;base64,")
    const base64Content = base64String.split(',')[1] || base64String;
    return base64Content;
};

/**
 * Gets the SHA of an existing file if it exists
 */
const getFileSha = async (
    token: string,
    branchName: string,
    filePath: string
): Promise<string | null> => {
    try {
        const response = await fetch(
            `${GITHUB_API_BASE}/repos/${REPO_OWNER}/${REPO_NAME}/contents/${filePath}?ref=${branchName}&_t=${Date.now()}`,
            {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept': 'application/vnd.github.v3+json',
                    'If-None-Match': ''
                },
                cache: 'no-store'
            }
        );
        
        if (response.ok) {
            const data = await response.json();
            return data.sha;
        }
        
        return null;
    } catch (error) {
        console.log(`File ${filePath} does not exist yet, will create new`);
        return null;
    }
};

/**
 * Creates or updates a file in the repository
 */
const createOrUpdateFile = async (
    token: string,
    branchName: string,
    filePath: string,
    content: string,
    message: string,
    retryCount: number = 0
): Promise<void> => {
    const existingSha = await getFileSha(token, branchName, filePath);
    
    const body: any = {
        message: message,
        content: content,
        branch: branchName
    };
    
    if (existingSha) {
        body.sha = existingSha;
        console.log(`Updating existing file ${filePath} with SHA ${existingSha.substring(0, 7)}...`);
    } else {
        console.log(`Creating new file ${filePath}...`);
    }
    
    const response = await fetch(
        `${GITHUB_API_BASE}/repos/${REPO_OWNER}/${REPO_NAME}/contents/${filePath}`,
        {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Accept': 'application/vnd.github.v3+json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(body)
        }
    );
    
    if (response.status === 409 && retryCount < 2) {
        console.warn(`SHA conflict for ${filePath}, retrying (${retryCount + 1}/2)...`);
        await new Promise(r => setTimeout(r, 1000));
        return createOrUpdateFile(token, branchName, filePath, content, message, retryCount + 1);
    }
    
    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to create/update file ${filePath}: ${response.statusText}. Details: ${errorText}`);
    }
    
    console.log(`Successfully ${existingSha ? 'updated' : 'created'} ${filePath}`);
};

/**
 * Main function to submit questionnaire to GitHub
 * @param formData - The questionnaire form data
 * @param existingBranchName - Optional: If provided, uses this existing branch instead of creating a new one
 * @param storageContext - Which IndexedDB store to read images from
 * @param changedImageKeys - If provided, only push images whose keys are in this set (used by editor to skip unchanged images)
 */
export const submitQuestionnaireToGitHub = async (
    formData: QuestionnaireFormData, 
    existingBranchName?: string,
    storageContext: StorageContext = 'questionnaire',
    changedImageKeys?: Set<ImageKey>
): Promise<string> => {
    try {
        // Step 1: Get GitHub token
        console.log('Getting GitHub token...');
        const { token } = await getSupertoken();
        
        let branchName: string;
        
        if (existingBranchName) {
            // Use the provided existing branch name
            branchName = existingBranchName;
            console.log(`Using existing branch: ${branchName}`);
        } else {
            // Step 2: Generate branch name
            branchName = generateBranchName(formData);
            console.log(`Generated branch name: ${branchName}`);
            
            // Step 3: Get base branch SHA
            console.log('Getting base branch SHA...');
            const baseSha = await getBaseBranchSha(token);
            
            // Step 4: Create new branch
            console.log('Creating new branch...');
            await createBranch(token, branchName, baseSha);
        }
        
        // Step 5: Prepare config data (without images)
        const configData = prepareFormDataForGitHub(formData);
        const configJson = JSON.stringify(configData, null, 2);
        console.log('Config JSON:', configJson);
        const configBase64 = btoa(
            Array.from(new TextEncoder().encode(configJson), byte => String.fromCharCode(byte)).join('')
        );
        
        // Step 6: Push config.json to GitHub
        console.log('Pushing config.json...');
        await createOrUpdateFile(
            token,
            branchName,
            `${baseMainConfigPath}/config.json`,
            configBase64,
            // @ts-ignore
            `Add configuration for ${formData.basicInfo.coupleName1} ${formData.basicInfo.coupleName2}`
        );
        
        // Step 7: Load images from IndexedDB and push only changed ones
        const allImageEntries: ImagePushEntry[] = [
            { key: ImageKey.DESKTOP_IMAGE, path: `${baseImagePushPath}/desktopImage.png`, name: 'Desktop image', formHasImage: !!formData.mainImages.desktopImage },
            { key: ImageKey.MOBILE_IMAGE, path: `${baseImagePushPath}/mobileImage.png`, name: 'Mobile image', formHasImage: !!formData.mainImages.mobileImage },
            { key: ImageKey.WITNESS1_IMAGE, path: `${baseImagePushPath}/witness1.png`, name: 'Witness 1 image', formHasImage: !!formData.witnesses.witness1.image },
            { key: ImageKey.WITNESS2_IMAGE, path: `${baseImagePushPath}/witness2.png`, name: 'Witness 2 image', formHasImage: !!formData.witnesses.witness2.image },
        ];

        for (const entry of allImageEntries) {
            if (!entry.formHasImage) continue;
            if (changedImageKeys && !changedImageKeys.has(entry.key)) {
                console.log(`Skipping unchanged ${entry.name}`);
                continue;
            }

            const imageData = await getImage(entry.key, storageContext);
            if (imageData) {
                console.log(`Pushing ${entry.name}...`);
                const imageBase64 = extractBase64Content(imageData);
                await createOrUpdateFile(token, branchName, entry.path, imageBase64, `Add ${entry.name}`);
            }
        }
        
        console.log('Successfully submitted questionnaire to GitHub!');
        
        if (storageContext === 'questionnaire') {
            try {
                localStorage.removeItem(STORAGE_KEY);
                console.log('Questionnaire data cleared from localStorage');
            } catch (storageError) {
                console.warn('Failed to clear localStorage:', storageError);
            }
            
            try {
                await clearAllImages(storageContext);
                console.log('Images cleared from IndexedDB');
            } catch (storageError) {
                console.warn('Failed to clear IndexedDB:', storageError);
            }
        }
        
        return branchName;
    } catch (error) {
        console.error('Error submitting questionnaire to GitHub:', error);
        throw error;
    }
};
